import Vue from 'vue'
import Vuex from 'vuex'
import { USER_INFO } from '@/apis/user'
import { get } from '@/utils/http'

Vue.use(Vuex)

// 支持的语言列表(与 localeArr/langs 保持一致)
const SUPPORTED_LANGS = ['zh-CN', 'en-US', 'lt-LT', 'es-ES', 'pt-PT', 'fr-FR', 'de-DE', 'it-IT']

// 根据手机/浏览器默认语言自动选择显示语言:优先精确匹配(zh-CN),其次语言前缀匹配(zh-TW→zh-CN、pt-BR→pt-PT),
// 都没有匹配(如缅甸语 my-MM)时默认英语
const detectDefaultLang = () => {
  const navLangs = navigator.languages || [navigator.language || navigator.userLanguage].filter(Boolean)
  for (const navLang of navLangs) {
    if (!navLang) continue
    if (SUPPORTED_LANGS.includes(navLang)) return navLang
    const prefix = navLang.split('-')[0].toLowerCase()
    const hit = SUPPORTED_LANGS.find(l => l.split('-')[0].toLowerCase() === prefix)
    if (hit) return hit
  }
  return 'en-US'
}

export default new Vuex.Store({
  state: {
    safeArea: JSON.parse(localStorage.getItem('cdb_safe_area')) || { top: 0, bottom: 0 },
    user: JSON.parse(localStorage.getItem('cdb_user')),
    userPwd: JSON.parse(localStorage.getItem('cdb_user_pwd')),
    token: localStorage.getItem('cdb_token'),
    loading: false,
    appleIdToken: '',
    urlcode: sessionStorage.getItem('cdb_urlcode'),
    qrcode: sessionStorage.getItem('cdb_qrcode'),
    zhcode: sessionStorage.getItem('cdb_zhcode'),
    mificode: sessionStorage.getItem('cdb_mificode'),
    mifiPackage: JSON.parse(sessionStorage.getItem('cdb_package')) || '',
    mifiOrder: JSON.parse(sessionStorage.getItem('cdb_mifiorder')) || '',
    mifisn: sessionStorage.getItem('cdb_mifisn') || '',
    zujieOrder: JSON.parse(sessionStorage.getItem('cdb_zujieorder')) || '',
    // 已手动选择过语言(cdb_lang)时优先;否则跟随手机/浏览器默认语言,无匹配默认英语
    lang: localStorage.getItem('cdb_lang') || detectDefaultLang(),
    system: localStorage.getItem('cdb_system') || '',
    browser: localStorage.getItem('cdb_browser') || '',
    cabinet: JSON.parse(localStorage.getItem("cdb_cabinet")) || null,
    localeArr: [
      { key: 0, label: '简体中文', value: 'zh-CN', currency: '元', money: '￥' },
      { key: 1, label: 'English', value: 'en-US', currency: 'dollar', money: '$' },
      { key: 2, label: 'Lithuanian', value: 'lt-LT', currency: 'LTL', money: 'Lt' },
      { key: 3, label: 'Português', value: 'pt-PT', currency: 'EUR', money: '€' },
      { key: 4, label: "Spanish", value: "es-ES", currency: "EUR", money: "€" },
      { key: 5, label: "Français", value: "fr-FR", currency: "EUR", money: "€" },
      { key: 6, label: "Deutsch", value: "de-DE", currency: "EUR", money: "€" },
      { key: 7, label: "Italiano", value: "it-IT", currency: "EUR", money: "€" }
    ],
    wallet: sessionStorage.getItem('cdb_wallet') ? JSON.parse(sessionStorage.getItem('cdb_wallet')) : null,
    // agentOpenId: '242747041607252',
    // agentOpenId: 'BJCD0000000000000000000000000001',
    agentOpenId: 'oDn2e4hi8M1iVtXcxm1uYpKTPEBE',
    userType: (() => {
      // 兼容历史版本写入的原始字符串(非 JSON 格式会 JSON.parse 报错导致白屏),优先读 sessionStorage(mutation 写入处)
      const raw = sessionStorage.getItem('cdb_user_type') || localStorage.getItem('cdb_user_type')
      if (!raw) return null
      try {
        return JSON.parse(raw)
      } catch (e) {
        return raw
      }
    })(), // 游客类型(tourist_login)
    homePathName: localStorage.getItem('cdb_homename') || 'home',
    applyLogin: localStorage.getItem('apply_login'),
    payType: localStorage.getItem("cdb_payType") || ""
  },
  getters: {
    locale: state => {
      return state.localeArr.find(obj => obj.value === state.lang)
    }
  },
  mutations: {
    safeArea(state, payload) {
      localStorage.setItem('cdb_safe_area', JSON.stringify(payload))
      state.safeArea = payload
    },
    user(state, payload) {
      localStorage.setItem('cdb_user', JSON.stringify(payload))
      state.user = payload
    },
    userPwd(state, payload) {
      localStorage.setItem('cdb_user_pwd', JSON.stringify(payload))
      state.userPwd = payload
    },
    token(state, payload) {
      localStorage.setItem('cdb_token', payload)
      state.token = payload
    },
    appleIdToken(state, payload) {
      state.appleIdToken = payload
    },
    loading(state, payload) {
      state.loading = payload
    },
    urlcode(state, payload) {
      sessionStorage.setItem('cdb_urlcode', payload)
      state.urlcode = payload
    },
    qrcode(state, payload) {
      sessionStorage.setItem('cdb_qrcode', payload)
      state.qrcode = payload
    },
    zhcode(state, payload) {
      sessionStorage.setItem('cdb_zhcode', payload)
      state.zhcode = payload
    },
    mificode(state, payload) {
      sessionStorage.setItem('cdb_mificode', payload)
      state.mificode = payload
    },
    mifiPackage(state, payload) {
      sessionStorage.setItem('cdb_package', JSON.stringify(payload))
      state.mifiPackage = payload
    },
    mifiOrder(state, payload) {
      sessionStorage.setItem('cdb_mifiorder', JSON.stringify(payload))
      state.mifiOrder = payload
    },
    mifisn(state, payload) {
      sessionStorage.setItem('cdb_mifisn', payload)
      state.mifisn = payload
    },
    zujieOrder(state, payload) {
      sessionStorage.setItem('cdb_zujieorder', JSON.stringify(payload))
      state.zujieOrder = payload
    },
    lang(state, payload) {
      localStorage.setItem('cdb_lang', payload)
      state.lang = payload
    },
    system(state, payload) {
      localStorage.setItem('cdb_system', payload)
      state.system = payload
    },
    browser(state, payload) {
      localStorage.setItem('cdb_browser', payload)
      state.browser = payload
    },
    cabinet(state, payload) {
      localStorage.setItem("cdb_cabinet", JSON.stringify(payload));
      state.cabinet = payload
    },
    wallet(state, payload) {
      sessionStorage.setItem('cdb_wallet', JSON.stringify(payload))
      state.wallet = payload
    },
    userType(state, payload) {
      sessionStorage.setItem('cdb_user_type', JSON.stringify(payload))
      state.userType = payload
    },
    homePathName(state, payload) {
      localStorage.setItem('cdb_homename', payload)
      state.homePathName = payload
    },
    applyLogin(state, payload) {
      localStorage.setItem('apply_login', payload)
      state.applyLogin = payload
    },
    payType(state, payload) {
      localStorage.setItem("cdb_payType", payload);
      state.payType = payload;
    }
  },
  actions: {
    loadUser({ commit }) {
      return new Promise((resolve, reject) => {
        get(
          USER_INFO,
          {},
          resp => {
            if (resp.user) {
              commit('user', resp.user)
              resolve(resp.user)
            } else {
              reject(new Error('Login Failed'))
            }
          },
          error => {
            reject(error)
          }
        )
      })
    }
  }
})
